/**
 * Search plus one or two selects, above a deposit table.
 *
 * Client-side by design: both endpoints answer with the whole portfolio in one
 * response and neither offers pagination, so a server round trip per keystroke
 * would buy nothing and cost a spinner.
 *
 * The result count is stated in words next to the controls. Without it a filter
 * that matches nothing looks identical to a table that failed to load.
 */
import React from "react";
import { Search } from "lucide-react";

export default function DepositsFilterBar({
  searchValue,
  onSearchChange,
  searchLabel,
  searchPlaceholder,
  resultLabel,
  children,
  testId,
}) {
  return (
    <div
      data-testid={testId}
      className="flex flex-wrap items-center gap-2 border-b border-border pb-3"
    >
      <div className="relative min-w-0 flex-1 basis-48">
        <Search
          aria-hidden="true"
          className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
        />
        <input
          type="search"
          aria-label={searchLabel}
          value={searchValue}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder={searchPlaceholder}
          className="w-full rounded-lg border border-border bg-background py-1.5 pl-8 pr-3 text-[12.5px] text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
      </div>

      {children}

      {resultLabel ? (
        <span className="ml-auto shrink-0 text-[11px] text-muted-foreground">
          {resultLabel}
        </span>
      ) : null}
    </div>
  );
}

/** A labelled select, sized to sit beside the search box. */
export function DepositsFilterSelect({ label, value, onChange, options }) {
  return (
    <label className="inline-flex shrink-0 items-center gap-1.5 text-[11px] text-muted-foreground">
      <span className="sr-only lg:not-sr-only">{label}</span>
      <select
        aria-label={label}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="rounded-lg border border-border bg-background px-2 py-1.5 text-[12.5px] text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}
